// The pharmacy: item search, the counter sale, returns and item settings
// (docs/plans/phase1-dispensing.md §5). Search terms and printed numbers travel in POST
// bodies, never in URLs. Every write carries a client request key.
import { queryOptions } from '@tanstack/react-query';
import {
  itemPharmacySettingsListSchema,
  itemPharmacySettingsSchema,
  pharmacyItemListSchema,
  returnableSchema,
  returnListSchema,
  returnViewSchema,
  saleListSchema,
  salePreviewSchema,
  saleViewSchema,
  storeSchema,
  type barcodesBodySchema,
  type createReturnBodySchema,
  type createSaleBodySchema,
  type itemPharmacySettingsSearchBodySchema,
  type salePreviewBodySchema,
  type supplyClassBodySchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

export const pharmacyKeys = {
  all: ['pharmacy'] as const,
  sales: (storeId: string) => ['pharmacy', 'sales', storeId] as const,
  sale: (id: string) => ['pharmacy', 'sale', id] as const,
  returns: (status: string) => ['pharmacy', 'returns', status] as const,
  item: (id: string) => ['pharmacy', 'item', id] as const,
  itemSettings: (filter: object) => ['pharmacy', 'item-settings', filter] as const,
};

export const searchPharmacyItems = (storeId: string, query: string) =>
  api('POST', '/pharmacy/items/search', {
    body: { storeId, query },
    schema: pharmacyItemListSchema,
  }).then((r) => r.items);

export const previewSale = (body: In<typeof salePreviewBodySchema>) =>
  api('POST', '/pharmacy/sales/preview', { body, schema: salePreviewSchema });

export const createSale = (body: In<typeof createSaleBodySchema>) =>
  api('POST', '/pharmacy/sales', { body, schema: saleViewSchema });

export const salesQuery = (storeId: string) =>
  queryOptions({
    queryKey: pharmacyKeys.sales(storeId),
    queryFn: () =>
      api('GET', `/pharmacy/sales?storeId=${storeId}`, { schema: saleListSchema }).then(
        (r) => r.items,
      ),
  });

export const saleQuery = (id: string) =>
  queryOptions({
    queryKey: pharmacyKeys.sale(id),
    queryFn: () => api('GET', `/pharmacy/sales/${id}`, { schema: saleViewSchema }),
  });

export const findReturnable = (sourceType: 'dispense' | 'sale', number: string) =>
  api('POST', '/pharmacy-returns/returnable', {
    body: { sourceType, number },
    schema: returnableSchema,
  });

export const requestReturn = (body: In<typeof createReturnBodySchema>) =>
  api('POST', '/pharmacy-returns', { body, schema: returnViewSchema });

export const returnsQuery = (status: 'requested' | 'all') =>
  queryOptions({
    queryKey: pharmacyKeys.returns(status),
    queryFn: () =>
      api('GET', status === 'all' ? '/pharmacy-returns' : `/pharmacy-returns?status=${status}`, {
        schema: returnListSchema,
      }).then((r) => r.items),
  });

export const decideReturn = (
  id: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/pharmacy-returns/${id}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: returnViewSchema,
  });

export const itemPharmacyQuery = (itemId: string) =>
  queryOptions({
    queryKey: pharmacyKeys.item(itemId),
    queryFn: () =>
      api('GET', `/stock-items/${itemId}/pharmacy`, { schema: itemPharmacySettingsSchema }),
  });

/** Settings > Pharmacy items: stock items by name, code or barcode, with their supply class. */
export const searchItemSettings = (body: In<typeof itemPharmacySettingsSearchBodySchema>) =>
  api('POST', '/pharmacy/item-settings/search', {
    body,
    schema: itemPharmacySettingsListSchema,
  }).then((r) => r.items);

export const setSupplyClass = (itemId: string, body: In<typeof supplyClassBodySchema>) =>
  api('PUT', `/stock-items/${itemId}/supply-class`, { body, schema: itemPharmacySettingsSchema });

export const setBarcodes = (itemId: string, body: In<typeof barcodesBodySchema>) =>
  api('PUT', `/stock-items/${itemId}/barcodes`, { body, schema: itemPharmacySettingsSchema });

/** The facility's active pharmacy stores; readable with pharmacy.read alone. */
export const pharmacyStoresQuery = queryOptions({
  queryKey: ['pharmacy', 'stores'],
  queryFn: () => api('GET', '/pharmacy/stores', { schema: z.array(storeSchema) }),
});
